import { useEditPollModal } from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/EditPollModal";
import {
  pollKeys,
  useExportPollResponses,
  useUndoMutation,
} from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal/api";
import type { PollResponse } from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal/api";
import { BlockInViewOnlyMode } from "@circle-react/components/ViewOnlyMasquerading/BlockInViewOnlyMode";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { Box } from "@circleco/compass/components/Box";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Menu } from "@circleco/compass/components/Menu";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import { Typography } from "@circleco/compass/components/Typography";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { canManageSpace } from "@/react/helpers/spaceHelpers";

import { PollFooter } from "./PollFooter";
import { PollOption } from "./PollOption";

const UNDO_TIMEOUT = 15000;

export const PollWrapper = ({
  poll,
  isEditable,
  deleteNode,
}: {
  isEditable: boolean;
  poll: PollResponse;
  deleteNode: () => void;
}) => {
  const queryClient = useQueryClient();
  const { currentCommunityMember } = usePunditUserContext();
  const { data: currentSpace } = useCurrentSpaceContext();
  const canManageCurrentSpace = canManageSpace(currentSpace);
  const { handleExportPollResponses, isExporting } = useExportPollResponses();
  const editPollModal = useEditPollModal();
  const [canRespond, setCanRespond] = useState<boolean>(
    !poll.community_member_response_option_id && !!currentCommunityMember,
  );
  const [selectedOptionId, setSelectedOptionId] = useState<number | undefined>(
    poll.community_member_response_option_id,
  );
  const shouldShowResults = Boolean(
    poll.results && "detailed_results" in poll.results,
  );

  const undoMutation = useUndoMutation({
    options: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: pollKeys.detail(poll.sgid),
        });
      },
    },
  });
  useEffect(() => {
    if (!selectedOptionId) return;

    const timerId = window.setTimeout(() => {
      setCanRespond(false);
    }, UNDO_TIMEOUT);

    return () => clearTimeout(timerId);
  }, [selectedOptionId]);

  const canUndo = Boolean(selectedOptionId && canRespond);
  const undo = () => {
    setSelectedOptionId(undefined);
    setCanRespond(true);
    undoMutation.mutate(poll.id);
  };

  const menuOptions: MenuOptions = [
    {
      label: t("tiptap.extensions.poll.edit"),
      hidden: !isEditable,
      onClick: () => {
        void editPollModal.show({
          pollSgid: poll.sgid,
        });
      },
    },
    {
      label: t("tiptap.extensions.poll.remove"),
      hidden: !isEditable,
      onClick: () => deleteNode(),
    },
    {
      label: t("tiptap.extensions.poll.export_responses_csv"),
      hidden: !canManageCurrentSpace,
      disabled: isExporting,
      onClick: () => handleExportPollResponses(poll.id),
    },
  ];
  const hasVisibleMenuOptions = isEditable || canManageCurrentSpace;

  return (
    <BlockInViewOnlyMode>
      <Box className="flex flex-col gap-5" data-testid="poll-wrapper">
        <div
          className="flex items-center justify-between gap-4"
          data-testid="poll-header"
        >
          <Typography
            className="!m-0 min-w-0 flex-1"
            component="h3"
            variant="heading-md"
          >
            {poll.title}
          </Typography>
          {hasVisibleMenuOptions && (
            <Menu
              options={menuOptions}
              trigger={
                <IconButton
                  aria-label={t("options_dropdown")}
                  className="shrink-0"
                  data-testid={`poll-${poll.id}-${isEditable ? "edit" : "view"}-actions`}
                  icon="dot-menu"
                  size="sm"
                  type="button"
                  variant="ghost"
                />
              }
            />
          )}
        </div>
        <div className="flex flex-col gap-4" data-testid="poll-options">
          {poll.poll_options.map(option => (
            <PollOption
              key={option.id}
              poll={poll}
              option={option}
              canRespond={canRespond}
              selectedOptionId={selectedOptionId}
              setSelectedOptionId={setSelectedOptionId}
              shouldShowResults={shouldShowResults}
            />
          ))}
        </div>
        <PollFooter poll={poll} canUndo={canUndo} undo={undo} />
      </Box>
    </BlockInViewOnlyMode>
  );
};
